// WHAT: Page component for daily plan view with shortfall calculation
// WHY:  Provides interface for viewing and managing daily work plan based on shortfalls
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: DailyPlanTab, useSupabaseTasks, useWorkspaceConfig, useDailyPlan, core.invoke
// RISKS:
//   - NOTE: Shortfall calculation depends on backend - may fail silently
//   - NOTE: View mode state is managed locally - may not persist

import type { ShortfallEntry } from "@/domains/productivity/timetracq/hooks/useDailyPlan";

import { core } from "@tauri-apps/api";
import React, { useState, useEffect } from "react";

import { useSupabaseTasks } from "../../hooks/data/useSupabasetasks";
import { useWorkspaceConfig } from "../../hooks/time_tracking/useWorkspaceConfig";
import { useDailyPlan } from "../../hooks/useDailyPlan";
import { DailyPlanTab } from "../../screen/tabs/DailyPlanTab";

/**
 * WHAT:
 *   Extracts a number value from primary or fallback source.
 *
 * WHY:
 *   Provides consistent number extraction logic for shortfall mapping.
 *
 * @param primary - Primary value to check
 *
 * @param fallback - Fallback value if primary is not a number
 *
 * @returns Number value or 0
 *
 */
const getNumberValue = (primary: unknown, fallback: unknown): number => {
  if (typeof primary === "number") {
    return primary;
  }
  if (typeof fallback === "number") {
    return fallback;
  }
  return 0;
};

/**
 * WHAT:
 *   Page component that displays daily plan based on calculated shortfalls and
 *   provides interface for managing daily work allocation.
 *
 * WHY:
 *   Enables users to view and manage their daily work plan based on shortfall
 *   calculations from workspace/aspect tracking.
 *
 * BEHAVIOR:
 *   - Loads tasks and workspace configuration
 *   - Calculates shortfalls via backend
 *   - Sorts shortfalls based on view mode (today/full)
 *   - Generates daily plan using useDailyPlan hook
 *   - Displays plan in DailyPlanTab component
 *
 * RISKS:
 *   - NOTE: Shortfall calculation depends on backend - may fail silently
 *   - NOTE: View mode state is managed locally - may not persist
 *
 * @returns {JSX.Element} The daily plan page component
 *
 */
export const DailyPlanPage: React.FC = () => {
  const { tasks } = useSupabaseTasks();
  const [config, , isLoading] = useWorkspaceConfig();
  const [shortfalls, setShortfalls] = useState<ShortfallEntry[]>([]);
  const [shortfallViewMode] = useState<"full" | "today">("today");
  const [workedToday, setWorkedToday] = useState<number>(0);

  // NOTE: Load shortfalls using the same approach as useDataFetcher
  useEffect(() => {
    const fetchShortfalls = async () => {
      if (tasks.length === 0) {
        return;
      }

      try {
        // NOTE: Calculate shortfalls via backend
        const raw = await core.invoke<{
          shortfalls: {
            parent: string;
            child: string;
            [key: string]: unknown;
          }[];
          worked_today: number;
        }>("calculate_shortfalls", { appHandle: {} });

        // NOTE: Map backend shortfall data to frontend format
        const mappedShortfalls: ShortfallEntry[] = raw.shortfalls.map((item) => {
          return {
            child: item.child,
            parent: item.parent,
            planned: getNumberValue(item.planned, item.planned_hours_monthly),
            plannedUntilToday: getNumberValue(item.plannedUntilToday, item.planned_until_today),
            shortfallFullMonth: getNumberValue(item.shortfallFullMonth, item.shortfall_full_month),
            shortfallUntilToday: getNumberValue(
              item.shortfallUntilToday,
              item.shortfall_until_today,
            ),
            status: typeof item.status === "string" ? item.status : "include",
            worked: getNumberValue(item.worked, item.total_hours_worked),
          };
        });

        // NOTE: Sort shortfalls by selected view mode (descending)
        mappedShortfalls.sort((a, b) => {
          const aValue =
            shortfallViewMode === "today" ? a.shortfallUntilToday : a.shortfallFullMonth;
          const bValue =
            shortfallViewMode === "today" ? b.shortfallUntilToday : b.shortfallFullMonth;
          return bValue - aValue;
        });

        setShortfalls(mappedShortfalls);
        setWorkedToday(raw.worked_today || 0);
      } catch {
        // NOTE: Error handling - reset to empty state
        setShortfalls([]);
        setWorkedToday(0);
      }
    };

    void fetchShortfalls();
  }, [tasks, shortfallViewMode]);

  // NOTE: Use the same daily plan logic as the main TimeTracQ app
  const { dailyCapacity, dailyPlan, shortfallSource } = useDailyPlan({
    config: config as Record<string, Record<string, { color: string; hours: number }>>,
    shortfalls,
    shortfallViewMode,
    tasks,
    workedToday, // Pass the worked hours
  });

  if (isLoading) {
    return <div className="text-white p-6">🔄 Lade Workspace-Konfiguration...</div>;
  }

  return (
    <DailyPlanTab
      dailyCapacity={dailyCapacity}
      dailyPlan={dailyPlan}
      shortfallSource={shortfallSource}
      workedToday={workedToday}
    />
  );
};
